import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Button, Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';

import {
  requiredString,
  requiredIfSectionTownshipRange,
  requiredIfLotBlock,
  isCountyRequired,
  requiredIfStreetAddress
} from 'common/helpers/yup-helpers';
import { useDisabledState } from 'common/disabled-context';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import CountiesAutocomplete from 'common/components/counties-autocomplete';
import { NotificationCard } from 'core/components/notification-card';

import useStyles from './location.styles';

const initialValues = {
  legalDescription: {
    format: undefined,
    section: undefined,
    township: undefined,
    townshipCoordinates: undefined,
    range: undefined,
    rangeCoordinates: undefined,
    lot: undefined,
    block: undefined
  },
  description: undefined,
  numberOfAcres: undefined,
  address: {
    address: undefined,
    address2: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    county: undefined
  }
};

Yup.addMethod(Yup.string, 'requiredIfSectionTownshipRange', requiredIfSectionTownshipRange);
Yup.addMethod(Yup.string, 'requiredIfLotBlock', requiredIfLotBlock);
Yup.addMethod(Yup.string, 'isCountyRequired', isCountyRequired);
Yup.addMethod(Yup.string, 'requiredIfStreetAddress', requiredIfStreetAddress);
Yup.addMethod(Yup.string, 'requiredString', requiredString);

const { coordinateOptions } = lookupsJson;
const { legalDescriptionFormatOptions } = lookupsJson.farm;

const formatPath = 'legalDescription.format';
const validationSchema = () =>
  Yup.object().shape({
    legalDescription: Yup.object().shape({
      lot: Yup.string().requiredIfLotBlock('Lot is required', formatPath),
      block: Yup.string().requiredIfLotBlock('Block is required', formatPath),
      section: Yup.string().requiredIfSectionTownshipRange('Section is required', formatPath),
      township: Yup.string().requiredIfSectionTownshipRange('Township is required', formatPath),
      range: Yup.string().requiredIfSectionTownshipRange('Range is required', formatPath),
      townshipCoordinates: Yup.string(),
      rangeCoordinates: Yup.string(),
      format: Yup.string().requiredString('Legal Description Format is required')
    }),
    address: Yup.object().shape({
      address: Yup.string().requiredIfStreetAddress('Address is required', formatPath),
      address2: Yup.string().nullable(),
      city: Yup.string().required('City is required', formatPath),
      state: Yup.string().required('State is required', formatPath),
      zip: Yup.string().required('ZIP is required', formatPath),
      county: Yup.string().isCountyRequired('County is required', formatPath)
    }),
    description: Yup.string().required('Location name is required'),
    numberOfAcres: Yup.number().required('Acreage is required')
  });

const AddLocation = observer(({ onAdd, disabled }) => {
  const classes = useStyles();
  const { activeForm } = useDisabledState();

  const disableForm = Boolean(activeForm || disabled);

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={onAdd}
    >
      {({ values, setFieldValue }) => {
        const legalDescriptionFormat = values?.legalDescription?.format;
        return (
          <Form className={classes.container}>
            <Label type="infoSubtitle">Add location</Label>
            <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
              <Field
                xs={4}
                type="select"
                name="legalDescription.format"
                mode="light"
                label="Legal description format*"
                options={legalDescriptionFormatOptions}
                onChange={(value) => {
                  // Default legalDescriptionFormat values
                  if (value === 'section-township-range') {
                    setFieldValue('legalDescription.townshipCoordinates', 'unknown');
                    setFieldValue('legalDescription.rangeCoordinates', 'unknown');
                  }
                }}
                disabled={disableForm}
              />
              <Field xs={4} type="numeric" name="numberOfAcres" mode="light" label="Acres*" disabled={disableForm} />
              <Field
                xs={4}
                type="string"
                name="description"
                mode="light"
                label="Location name*"
                disabled={disableForm}
              />

              {legalDescriptionFormat === 'street' && (
                <>
                  <Field
                    xs={8}
                    type="address"
                    name="address.address"
                    mode="light"
                    label="Address*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="string"
                    name="address.address2"
                    mode="light"
                    label="Apt, unit, building, etc."
                    disabled={disableForm}
                  />
                  <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                  <Field xs={4} type="state" name="address.state" mode="light" label="State*" disabled={disableForm} />
                  <Field
                    xs={4}
                    type="string"
                    name="address.zip"
                    mode="light"
                    label="Zip code*"
                    disabled={disableForm}
                  />
                </>
              )}

              {legalDescriptionFormat === 'section-township-range' && (
                <>
                  <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                  <Field xs={4} type="state" name="address.state" mode="light" label="State*" disabled={disableForm} />
                  <Field
                    xs={4}
                    type="string"
                    name="address.zip"
                    mode="light"
                    label="Zip code*"
                    disabled={disableForm}
                  />
                  <CountiesAutocomplete
                    id="address.county"
                    state={'IL'}
                    setValues={setFieldValue}
                    disabled={disableForm}
                  />
                  <Field
                    xs={4}
                    type="string"
                    name="legalDescription.section"
                    mode="light"
                    label="Section*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={2}
                    type="string"
                    name="legalDescription.township"
                    mode="light"
                    label="Township*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={2}
                    type="select"
                    name="legalDescription.townshipCoordinates"
                    mode="light"
                    label="Coordinates"
                    options={coordinateOptions}
                    disabled={disableForm}
                  />
                  <Field
                    xs={2}
                    type="string"
                    name="legalDescription.range"
                    mode="light"
                    label="Range*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={2}
                    type="select"
                    name="legalDescription.rangeCoordinates"
                    mode="light"
                    label="Coordinates"
                    options={coordinateOptions}
                    disabled={disableForm}
                  />
                </>
              )}

              {legalDescriptionFormat === 'lot-block' && (
                <>
                  <Field
                    xs={6}
                    type="numeric"
                    name="legalDescription.lot"
                    mode="light"
                    label="Lot*"
                    disabled={disableForm}
                  />
                  <Field
                    xs={6}
                    type="numeric"
                    name="legalDescription.block"
                    mode="light"
                    label="Block*"
                    disabled={disableForm}
                  />
                  <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                  <Field xs={4} type="state" name="address.state" mode="light" label="State*" disabled={disableForm} />
                  <Field
                    xs={4}
                    type="string"
                    name="address.zip"
                    mode="light"
                    label="Zip code*"
                    disabled={disableForm}
                  />
                  <CountiesAutocomplete
                    state={'IL'}
                    id="address.county"
                    setValues={setFieldValue}
                    disabled={disableForm}
                  />
                </>
              )}

              {legalDescriptionFormat === 'long-legal' && (
                <>
                  <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                  <Field xs={4} type="state" name="address.state" mode="light" label="State*" disabled={disableForm} />
                  <Field
                    xs={4}
                    type="string"
                    name="address.zip"
                    mode="light"
                    label="Zip code*"
                    disabled={disableForm}
                  />
                  <CountiesAutocomplete
                    state={'IL'}
                    setValues={setFieldValue}
                    id="address.county"
                    disabled={disableForm}
                  />
                </>
              )}

              {!!legalDescriptionFormat && (
                <>
                  <Field
                    xs={12}
                    type="string"
                    name="additionalLegalDescription"
                    mode="light"
                    label="Additional legal description (optional)"
                    disabled={disableForm}
                  />
                  <Field
                    xs={12}
                    type="string"
                    name="locationDirections"
                    mode="light"
                    label="Location directions (optional)"
                    disabled={disableForm}
                  />
                  <Grid item container justifyContent="flex-end" xs={12}>
                    <Button
                      type="submit"
                      key="addButton"
                      mode="big"
                      variant="contained"
                      color="secondary"
                      className={classes.button}
                      disabled={disableForm}
                    >
                      Add location
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
            {disableForm && (
              <NotificationCard className={classes.notification} iconSize="icon16" type="infoWhite">
                <Label>You can't add a location until you update the offer.</Label>
              </NotificationCard>
            )}
          </Form>
        );
      }}
    </Formik>
  );
});

AddLocation.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

AddLocation.defaultProps = {
  disabled: false
};

export default AddLocation;
